<style lang="scss" scoped>
@use 'sass:color';
@use '../../../palette.scss' as *;

.unit-details-container {
    .unit-details-card {
        display: flex;
        flex-direction: column;
        width: 100%;

        table {
            border: 1px solid color.adjust($theme-dark-grey, $lightness: 40%);

            .table-head {
                background-color: color.adjust(
                    $theme-dark-grey,
                    $lightness: 40%
                );
                color: white;
                font-size: 0.9em;
            }

            tr {
                position: relative;

                &.odd {
                    background-color: color.adjust(
                        $theme-light-grey,
                        $alpha: -0.8
                    );

                    &.alert {
                        background-color: color.adjust(
                            $theme-red,
                            $alpha: -0.7
                        );
                    }
                }

                td {
                    padding: 10px;

                    &.check {
                        .fa-exclamation-circle {
                            margin-left: 10px;
                        }

                        button {
                            display: inline-flex;
                            transform: translateX(100%);
                            opacity: 0;
                            transition: opacity 0.25s;
                        }
                    }
                }

                &.alert {
                    background-color: color.adjust($theme-red, $alpha: -0.75);
                }

                &:hover {
                    td.check button {
                        opacity: 1;
                    }
                }
            }
        }

        .control-row {
            display: flex;
            align-items: center;
            padding: 8px 0;
            margin-bottom: 20px;
            z-index: 25;

            .dropdown-container {
                min-width: 200px;
            }

            & > .button {
                margin-left: 10px;
            }
        }

        .image-ctn {
            display: flex;
            justify-content: center;
            position: relative;
            flex-grow: 1;
            margin-top: 20px;

            .sensor-box {
                position: absolute;
                z-index: 5;
            }

            img,
            .sensor-box {
                width: 187px;
                height: 105px;
            }
        }

        /* Larger phones/small tablets */
        @media (min-width: 510px) {
            .image-ctn {
                img,
                .sensor-box {
                    width: 362px;
                    height: 203px;
                }
            }
        }

        /* Small devices (tablets, 768px and up) */
        @media (min-width: 768px) {
            .image-ctn {
                img,
                .sensor-box {
                    width: 382px;
                    height: 214px;
                }
            }
        }

        /* Medium devices (desktops, 992px and up) */
        @media (min-width: 992px) {
            .image-ctn {
                img,
                .sensor-box {
                    width: 556px;
                    height: 312px;
                }
            }
        }
    }
}
</style>

<template>
    <div class="unit-details-card">
        <table>
            <thead>
                <tr class="table-head">
                    <td>{{ $t('identifier') }}</td>
                    <td>{{ $t('name') }}</td>
                    <td>{{ $t('model') }}</td>
                    <td>{{ $t('monitored') }}</td>
                    <td>{{ $t('battery') }}</td>
                    <td>RSSI</td>
                    <td>{{ $t('age') }}</td>
                    <td>{{ $t('secure') }}</td>
                </tr>
            </thead>
            <tbody>
                <SensorTableEntry
                    v-for="(sensor, index) in sensors"
                    :readonly="readonly"
                    :key="sensor.node_id"
                    :sensor="sensor"
                    :sensorName="sensorNames[sensor.node_id]"
                    :tableIndex="index"
                    :monitored="getSensorMonitored(sensor.node_id)"
                    :pendingMonitored="
                        getSensorPendingMonitored(sensor.node_id)
                    "
                    :onMonitorChanged="onSensorMonitorChanged"
                    :onNameChanged="onSensorNameChanged"
                />
            </tbody>
        </table>
        <div class="control-row" v-if="!readonly">
            <DropdownMenu
                :options="imageOptions"
                outline
                @change="setContainerImage"
                :value="selectedImage"
                max-height="250px"
                :disabled="isCampaignViewer"
            />
        </div>
        <div class="image-ctn" ref="image" v-if="sensorImage">
            <img :src="sensorImage" />
            <div class="sensor-box">
                <Sensor
                    v-for="sensor in diagramSensors"
                    :sensor="sensor"
                    :key="sensor.id"
                    :draggable="editable"
                    @drag="moveSensor"
                ></Sensor>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

import {
    getSensorImageList,
    updateUiData,
    setSensorConfigs,
} from '../../../api/units';
import { STATUS_ERROR } from '../../../api/client';
import { getAgeString, sensorInAlarm } from '../../../util/util';
import SensorTableEntry from './SensorTableEntry.vue';

export default {
    props: {
        unit: {
            type: Object,
            required: true,
        },
        sensors: Array,
        config: Object,
        editable: Boolean,
        readonly: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            images: [],
            ageLoop: undefined,
            sensorAges: {},
            sensorEdits: {},
            sensorMonitorChanges: {},
            containerImageEdit: null,
        };
    },

    computed: {
        ...mapGetters(['isUserAdmin', 'isCampaignViewer']),

        imageOptions() {
            return this.images
                .map((o) => {
                    // On the android app, images are sent without file extensions, so we've got to format the same here
                    const pieces = o.split('.');
                    const text = pieces.slice(0, pieces.length - 1).join('');
                    // Replace dashes with spaces and make sure values are capitalized
                    let label = text.replace(/-/g, ' ');
                    label = label.charAt(0).toUpperCase() + label.slice(1);
                    return { label, value: text };
                })
                .sort((a, b) => {
                    if (a.label < b.label) {
                        return -1;
                    } else if (a.label > b.label) {
                        return 1;
                    }
                    return 0;
                });
        },

        saveState() {
            if (this.saveInProgress) {
                return 'sending';
            } else if (this.saveFailed) {
                return 'retry';
            } else if (this.saveSucceeded && !this.edits) {
                return 'success';
            } else if (this.edits) {
                return 'changes';
            }

            return 'hidden';
        },

        selectedImage() {
            if (this.containerImageEdit) {
                return this.containerImageEdit;
            } else if (this.uiData && this.uiData.container_image) {
                return this.uiData.container_image;
            }
            return 'Container-03';
        },

        sensorImage() {
            if (this.selectedImage) {
                return `../../../../assets/container_images/${this.selectedImage}.png`;
            } else {
                return '';
            }
        },

        sensorNames() {
            const names = {};
            const edits = this.sensorEdits;

            // Iterate through edits first since otherwise edits aren't picked up by the Notified User
            for (const id of Object.keys(edits)) {
                if (edits[id].name) {
                    names[id] = edits[id].name;
                }
            }

            for (const sensor of this.sensors) {
                const id = sensor.node_id;

                // Only pull from UIData if it exists and we don't already have a name value
                if (!names[id]) {
                    const data = this.getSensorUiData(id);
                    if (data && data.name) {
                        names[id] = data.name;
                    } else {
                        names[id] = '';
                    }
                }
            }
            return names;
        },

        diagramSensors() {
            const sensors = [];
            const offsetPercent = 0.5;
            if (this.sensors) {
                for (const sensor of this.sensors) {
                    const config = this.getSensorConfig(sensor.node_id);
                    if (config.monitor_settings.monitor) {
                        // If the sensor shows up, check to see if we have a position for it
                        const uiData = this.getSensorUiData(sensor.node_id);
                        let offsetX = offsetPercent;
                        let offsetY = offsetPercent;
                        if (uiData.position_x) {
                            offsetX = uiData.position_x;
                            offsetY = uiData.position_y;
                        }

                        sensors.push({
                            id: sensor.node_id,
                            alarm: sensorInAlarm(sensor),
                            x: offsetX,
                            y: offsetY,
                            name: uiData.name ? uiData.name : '',
                        });
                    }
                }
            }
            return sensors;
        },

        sensorConfigs() {
            if (this.unit && this.unit.sensor_configs) {
                return this.unit.sensor_configs;
            }
            return null;
        },

        pendingSensorConfigs() {
            if (this.unit && this.unit.pending_sensor_configs) {
                return this.unit.pending_sensor_configs;
            }
            return null;
        },

        uiData() {
            if (this.unit && this.unit.ui_data) {
                return this.unit.ui_data;
            }
            return null;
        },
    },

    created: async function () {
        const response = await getSensorImageList();
        if (response) {
            this.images = response.payload;
        }
        this.updateSensorAges();

        this.ageLoop = setTimeout(this.updateSensorAges, 1000);
    },

    beforeDestroy() {
        if (this.ageLoop) {
            clearTimeout(this.ageLoop);
            this.ageLoop = undefined;
        }
    },

    methods: {
        moveSensor(id, x, y) {
            // Update out local edits object with the new location of the sensor
            if (!this.sensorEdits[id]) {
                this.sensorEdits[id] = {};
            }

            // Spread required here to trigger the full update on our other computed properties
            this.sensorEdits = {
                ...this.sensorEdits,
                [id]: {
                    ...this.sensorEdits[id],
                    position_x: x,
                    position_y: y,
                },
            };

            this.$emit('commitEdits');
        },

        getSensorConfig(id) {
            if (this.sensorConfigs) {
                for (const config of this.sensorConfigs) {
                    if (config.node_id === id) {
                        return config;
                    }
                }
            }
            return {};
        },

        getSensorMonitored(id) {
            if (this.sensorMonitorChanges[id] != null) {
                return this.sensorMonitorChanges[id];
            }
            if (this.pendingSensorConfigs) {
                // Check to see if we've got a pending change for the sensor in question
                let config = null;
                for (const c of this.pendingSensorConfigs) {
                    if (c.node_id === id) {
                        return c.monitor_settings.monitor;
                    }
                }
            }
            if (this.sensorConfigs) {
                // Check to see if we've got any value at all for this sensor
                let config = null;
                for (const c of this.sensorConfigs) {
                    if (c.node_id === id) {
                        return c.monitor_settings.monitor;
                    }
                }
            }
            return false;
        },

        // This method returns a bool if the actual value for monitored differs from pending or current edit or null otherwise
        getSensorPendingMonitored(id) {
            let actual = null;
            if (this.sensorConfigs) {
                for (const c of this.sensorConfigs) {
                    if (c.node_id === id) {
                        actual = c.monitor_settings.monitor;
                        break;
                    }
                }
            }

            if (this.pendingSensorConfigs) {
                for (const c of this.pendingSensorConfigs) {
                    if (
                        c.node_id === id &&
                        actual !== c.monitor_settings.monitor
                    ) {
                        return actual;
                    }
                }
            }

            return null;
        },

        getSensorUiData(id) {
            let edits = {};
            let uiData = {};

            // edits = this.sensorEdits[id] != null ? this.sensorEdits[id] : {};
            if (this.sensorEdits[id]) {
                // return this.sensorEdits[id];
                edits = this.sensorEdits[id];
            }
            if (this.uiData && this.uiData.sensors) {
                for (const data of this.uiData.sensors) {
                    if (data.node_id === id) {
                        // return data;
                        uiData = data;
                        break;
                    }
                }
            }

            // Now that we've got local edits and global ui data, splice them together, prioritizing edits
            return {
                ...uiData,
                ...edits,
                node_id: id,
            };
        },

        setContainerImage(value) {
            this.containerImageEdit = value;
            this.$emit('commitEdits');
        },

        onSensorNameChanged(id, name) {
            if (!this.sensorEdits[id]) {
                this.sensorEdits[id] = {};
            }

            this.sensorEdits = {
                ...this.sensorEdits,
                [id]: {
                    ...this.sensorEdits[id],
                    name,
                },
            };

            this.$emit('commitEdits');
        },

        onSensorMonitorChanged(id, value) {
            // We do the spread reassignment here to make Vue trigger a watch change on the reference
            this.sensorMonitorChanges = {
                ...this.sensorMonitorChanges,
                [id]: !!value,
            };
            this.$emit('commitEdits');
        },

        // Run every second, updates the age strings for all sensors
        updateSensorAges() {
            for (const sensor of this.sensors) {
                if (sensor.last_update) {
                    const sensorAge =
                        new Date().valueOf() -
                        new Date(sensor.last_update).valueOf();
                    // Use set here so that Vue begins to track the new properties on the object
                    this.sensorAges[sensor.id] = getAgeString(sensorAge, true);
                } else {
                    this.sensorAges[sensor.id] = this.$t('na');
                }
            }

            if (this.ageLoop) {
                this.ageLoop = setTimeout(this.updateSensorAges, 1000);
            }
        },

        resetChanges() {
            window.location.reload();
        },

        async saveChanges() {
            const saveStatus = {
                saveFailed: false,
                saveSucceeded: false,
                saveFailText: '',
            };

            // Combine any edits we've been batching with existing sensor data and send it off
            const uiData = {};

            let makeUiCall = false;

            if (this.uiData) {
                // On this page, we don't edit campaign name, so it won't have been changed from here
                uiData.campaign_name = this.uiData.campaign_name;

                if (this.containerImageEdit) {
                    makeUiCall = true;
                }
                // Update our container image, whether we've changed it or not it still needs to go to the server
                uiData.container_image = this.containerImageEdit
                    ? this.containerImageEdit
                    : this.uiData.container_image;
            }

            if (Object.keys(this.sensorEdits).length) {
                makeUiCall = true;
            }

            // Pull our sensor ui data from either edits or the existing data source, we need to send it regardless
            for (const sensor of this.sensors) {
                const data = this.getSensorUiData(sensor.node_id);

                if (!uiData.sensors) {
                    uiData.sensors = [];
                }

                uiData.sensors.push(data);
            }

            // If nothing changed, we doin't need to bother with making the call to update
            if (makeUiCall) {
                let errorResponse = null;
                const response = await updateUiData(this.unit.id, {
                    ui_data: uiData,
                });

                if (response.status === STATUS_ERROR) {
                    errorResponse = response;
                }

                if (errorResponse) {
                    if (response.msg) {
                        // this.saveFailText = this.$t(response.msg, response.msg_data);
                        saveStatus.saveFailText = this.$t(
                            response.msg,
                            response.msg_data,
                        );
                    }
                    saveStatus.saveFailed = true;
                    saveStatus.saveSucceeded = false;
                    return saveStatus;
                } else {
                    saveStatus.saveFailed = false;
                    saveStatus.saveSucceeded = true;
                    saveStatus.saveFailText = '';

                    // If our save was successful, clear out all of our edits so other peoples' edits will be visible in real time
                    this.sensorEdits = {};
                }
            }

            // If we haven't made any changes to sensors being monitored, then we don't need to bother with that call either
            const monitorKeys = Object.keys(this.sensorMonitorChanges);
            if (monitorKeys.length) {
                // Unit can only handle one sensor command at a time, so take just the first one.
                const sensor_configs = [];
                sensor_configs.push({
                    node_id: monitorKeys[0],
                    monitor_settings: {
                        monitor: this.sensorMonitorChanges[monitorKeys[0]],
                    },
                });

                let errorResponse = null;
                const response = await setSensorConfigs(this.unit.id, {
                    sensor_configs,
                });

                if (response.status === STATUS_ERROR) {
                    errorResponse = response;
                }

                if (errorResponse) {
                    if (response.msg) {
                        saveStatus.saveFailText = this.$t(
                            response.msg,
                            response.msg_data,
                        );
                    }
                    saveStatus.saveFailed = true;
                    saveStatus.saveSucceeded = false;
                } else {
                    saveStatus.saveFailed = false;
                    saveStatus.saveSucceeded = true;
                    saveStatus.saveFailText = '';

                    // If our save was successful, clear out all of our edits so other peoples' edits will be visible in real time
                    this.sensorMonitorChanges = {};
                }
            }

            return saveStatus;
        },
    },

    components: {
        SensorTableEntry: SensorTableEntry,
    },
};
</script>
